import { useMutation, useQuery, useQueryClient } from 'react-query'
import { t } from 'ttag'
import styled from 'styled-components'
import { useContext } from 'react'
import ReactMarkdown from 'react-markdown'

import { Flex } from '@h2/components/layout/flex'
import { api } from '@h2/api'
import { Loader } from '@h2/components/loading/loader'
import { Heading } from '@h2/components/typography/heading'
import { Modal } from '@h2/components/containers/modal'
import { Link } from '@h2/common/components/link'
import { Text } from '@h2/components/typography/text'
import { ClusterIcon } from '@h2/components/icons/cluster'
import { ChatIcon } from '@h2/components/icons/chat'
import { CheckCircleIcon } from '@h2/components/icons/checkCircle'
import { TimesCircleIcon } from '@h2/components/icons/timesCircle'
import { QuestionCircleIcon } from '@h2/components/icons/questionCircle'
import { SO90T, PB90T, PB97T, WHITE, PB } from '@h2/components/colors'
import { Globals } from '@h2/globals'
import { useNavigator } from '@h2/common/components/hooks/useNavigator'

import { NextStepsChoice } from './nextStepsChoice'
import { PreferencesSummary } from './preferencesSummaryCard'
import { FinancialSummaryCard } from './financialSummaryCard'
import { SkillsSummaryCard } from './skillsSummaryCard'

import { OutputChip } from '../outputChip'

const GridContainer = styled.div`
  background-color: ${PB97T};
  padding: 5px;
  display: flex;
  flex-wrap: wrap;
`
const UnboundGridSquare = styled.div`
  background-color: ${WHITE};
  padding: 10px;
  margin: 5px;
  min-height: 180px;
  flex-grow: 1;
  border: 1px solid ${PB90T};
  border-radius: 5px;
`
const GridSquare = styled(UnboundGridSquare)`
  width: calc(50% - 10px);
`
const RecomendationHistory = styled.div`
  margin: 5px;
  background-color: ${SO90T};
  width: calc(100% - 10px);
  padding: 10px;
  border-radius: 5px;
  border: 1px solid ${PB90T};
`
const StyledLink = styled(Link)`
  color: ${PB};
  border-color: ${PB90T};
  padding: 7.5px 15px;
`
const RecommendationDetailInner = ({
  objectId,
  objectType,
  toggleModal,
  isAcceptable
}) => {
  const { clusterTaxonomyShortTitle } = useContext(Globals)
  const navigator = useNavigator()
  const { data, isFetching } = useQuery(
    [`recommendationDetail`, objectId, objectType],
    () => api.fetch(`/compatibility/${objectType}/${objectId}`, { method: 'GET' })
  )
  const queryClient = useQueryClient()
  const { mutate: mutateAcceptance, isLoading: isAccepting } = useMutation(
    () => {
      return api.fetch(`/recommendations/accept/${objectType}/${objectId}`, {
        method: 'POST',
        body: {}
      })
    },
    {
      onSuccess: async () => {
        toggleModal()
        await queryClient.refetchQueries(['recommendations'], { refetchType: 'all' })
      },
      onError: () => {}
    }
  )
  const { mutate: mutateRejection, isLoading: isRejecting } = useMutation(
    () => {
      return api.fetch(`/recommendations/reject/${objectType}/${objectId}`, {
        method: 'POST',
        body: {}
      })
    },
    {
      onSuccess: () => {
        toggleModal()
        queryClient.refetchQueries(['recommendations'], { refetchType: 'all' })
      },
      onError: () => {}
    }
  )

  if (isFetching) {
    return (
      <Modal.Body>
        <Loader />
      </Modal.Body>
    )
  }

  if (!data) {
    return <div></div>
  }

  const inner = () => {
    switch (objectType?.toLowerCase()) {
      case 'navigator':
        return (
          <GridSquare>
            <Heading level="4">{t`Next Steps`}</Heading>
            {(!data.dateAccepted && data.history.length) ||
            data.isSystemRecommendation ? (
              <>
                <Flex justifyContent="center">
                  <Text weight="bold">
                    {t`Would you like to accept this recommendation?`}
                  </Text>
                </Flex>
                <Flex justifyContent="space-between" gap="15px" margin="15px 0 0 0">
                  <NextStepsChoice
                    color="red"
                    onClick={mutateRejection}
                    isLoading={isRejecting}
                    disabled={isRejecting || isAccepting}
                    icon={TimesCircleIcon}>
                    {t`Decline Recommendation`}
                  </NextStepsChoice>
                  <NextStepsChoice
                    color="green"
                    onClick={mutateAcceptance}
                    isLoading={isFetching}
                    disabled={isRejecting || isAccepting}
                    icon={CheckCircleIcon}>
                    {t`Accept Recommendation`}
                  </NextStepsChoice>
                </Flex>
              </>
            ) : null}
          </GridSquare>
        )
      default:
        return (
          <>
            {data.preferences && (
              <GridSquare>
                <PreferencesSummary
                  preferences={data.preferences}
                  objectType={objectType}
                />
              </GridSquare>
            )}
            {data.financial && (
              <GridSquare>
                <FinancialSummaryCard
                  objectType={objectType}
                  financial={data.financial}
                />
              </GridSquare>
            )}
            {data.skills && (
              <GridSquare>
                <SkillsSummaryCard objectType={objectType} skills={data.skills} />
              </GridSquare>
            )}
            {!(data.skills || data.financial || data.preferences) && data.description && (
              <UnboundGridSquare style={{ maxHeight: '60vh', overflow: 'scroll' }}>
                <Heading level="5">{t`Description`}</Heading>
                <ReactMarkdown>{data.description}</ReactMarkdown>
              </UnboundGridSquare>
            )}
            <GridSquare>
              <Heading level="4">{t`Next Steps`}</Heading>
              {((!data.dateAccepted && data.history.length) ||
                data.isSystemRecommendation) &&
              isAcceptable ? (
                <>
                  <Flex justifyContent="center">
                    <Text weight="bold">
                      {t`Would you like to accept this recommendation?`}
                    </Text>
                  </Flex>
                  <Flex justifyContent="space-between" margin="15px 0 0 0" gap="15px">
                    <NextStepsChoice
                      color="red"
                      onClick={mutateRejection}
                      isLoading={isRejecting}
                      disabled={isRejecting || isAccepting}
                      icon={TimesCircleIcon}>
                      {t`Decline Recommendation`}
                    </NextStepsChoice>
                    <NextStepsChoice
                      color="green"
                      onClick={mutateAcceptance}
                      isLoading={isAccepting}
                      disabled={isRejecting || isAccepting}
                      icon={CheckCircleIcon}>
                      {t`Accept Recommendation`}
                    </NextStepsChoice>
                    <NextStepsChoice
                      color="yellow"
                      onClick={() => navigator(data?.url)}
                      icon={QuestionCircleIcon}>
                      {' '}
                      {t`Not sure? Explore full profile here`}
                    </NextStepsChoice>
                  </Flex>
                </>
              ) : (
                <StyledLink href={data?.url} variant="outlined-cta">
                  {t`View Full Profile`}
                </StyledLink>
              )}
            </GridSquare>
          </>
        )
    }
  }

  return (
    <>
      <Flex
        justifyContent="space-between"
        padding="0 50px 0 0"
        margin="0 0 8px 0"
        alignItems="end">
        <Heading level="2" removeMargins={true}>
          {data.name}
        </Heading>
        {data?.url && data?.url !== '#' && (
          <StyledLink href={data?.url} variant="outlined-cta">
            {t`View Full Profile`}
          </StyledLink>
        )}
      </Flex>
      <Flex gap="15px">
        <OutputChip text={objectType} shouldCapitalize={true} size="large" />
        {data.primaryCluster && (
          <Flex alignItems="center">
            <ClusterIcon size="tiny" />
            <Text size="medium" margin="0 0 0 2px">
              <b>{clusterTaxonomyShortTitle}:</b> {data.primaryCluster.name}
            </Text>
          </Flex>
        )}
      </Flex>
      <GridContainer>
        {data.history.map(itm => (
          <RecomendationHistory key={itm.id}>
            <Heading level="5" removeMargins={true}>
              {t`Recommended by: ${itm.recommendedBy.name}`}
            </Heading>
            <Flex>
              <ChatIcon size="medium" />
              <Text size="medium" margin="5px 0 0 2px">
                {itm.comments}
              </Text>
            </Flex>
          </RecomendationHistory>
        ))}
        {inner()}
      </GridContainer>
    </>
  )
}
const RecommendationDetail = ({
  objectId,
  objectType,
  modalOpen,
  toggleModal,
  isAcceptable = true
}) => {
  return (
    <Modal
      isOpen={modalOpen}
      closeOnClickOverlay={true}
      onClose={toggleModal}
      hasBorders={true}>
      <Modal.Header>{''}</Modal.Header>
      <Modal.Body>
        <RecommendationDetailInner
          isAcceptable={isAcceptable}
          objectId={objectId}
          objectType={objectType}
          toggleModal={toggleModal}
        />
      </Modal.Body>
    </Modal>
  )
}

export { RecommendationDetail }
